import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';

import ordersApi from '../../../api/ordersApi';
import { getBlobErrorMessage } from '../../../utils/errorHandler';
import { triggerBlobDownload } from '../../../utils/downloadFile';
import { paymentMethodLabel } from '../../../utils/paymentMethods';
import { shippingMethodLabel } from '../../../utils/shippingMethods';
import { ORDER_STATUS_COLORS, orderStatusLabel } from '../../../utils/orderStatuses';
import StatusProgress from './StatusProgress';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency: currency || 'PEN' }).format(
    value || 0
  );

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—');

const ACCEPTED_INVOICE = ['aceptada', 'observada'];

// Seguimiento de un pedido para el cliente: recibido -> pago confirmado -> comprobante emitido.
const ORDER_STEPS = [
  { key: 'recibido', label: 'Pedido recibido' },
  { key: 'pagado', label: 'Pago confirmado' },
  { key: 'comprobante', label: 'Comprobante emitido' },
];

const progressIndex = (order) => {
  const paid = order.status?.name === 'cancelado';
  const invoiced = ACCEPTED_INVOICE.includes(order.invoice?.status);
  if (paid && invoiced) return 2;
  if (paid) return 1;
  return 0;
};

// Tarjeta de un pedido en el panel del cliente / "Mis pedidos": estado, productos, total y la
// descarga de su boleta/factura cuando ya fue emitida.
export default function CustomerOrderCard({ order, compact }) {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  const invoiceReady = ACCEPTED_INVOICE.includes(order.invoice?.status);
  const isVoided = order.status?.name === 'anulado';

  const handleDownloadInvoice = async () => {
    setDownloading(true);
    try {
      const response = await ordersApi.downloadInvoicePdf(order.id);
      triggerBlobDownload(response.data, `${order.invoice.type}-${order.invoice.full_number}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el comprobante.'), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Stack>
            <Typography variant="subtitle1">Pedido N.° {order.id}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(order.created_at)} · {paymentMethodLabel(order.payment_method)} ·{' '}
              {shippingMethodLabel(order.shipping_method)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={orderStatusLabel(order.status?.name)}
            color={ORDER_STATUS_COLORS[order.status?.name] || 'default'}
          />
        </Stack>

        {isVoided ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            Este pedido fue anulado.
          </Alert>
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <StatusProgress steps={ORDER_STEPS} activeIndex={progressIndex(order)} />
          </Box>
        )}

        {!compact && (
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {(order.items || []).map((item) => (
              <Stack key={item.id} direction="row" sx={{ justifyContent: 'space-between' }} spacing={2}>
                <Typography variant="body2">
                  {item.quantity} × {item.product?.name || item.description || 'Producto'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMoney(item.subtotal, order.currency)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}

        <Divider sx={{ mb: 1.5 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Typography variant="subtitle2">Total: {formatMoney(order.total, order.currency)}</Typography>
          {invoiceReady && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadInvoice}
              disabled={downloading}
            >
              {downloading
                ? 'Descargando...'
                : `Descargar ${order.invoice.type === 'factura' ? 'factura' : 'boleta'} ${order.invoice.full_number}`}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

CustomerOrderCard.propTypes = {
  order: PropTypes.object.isRequired,
  compact: PropTypes.bool,
};

CustomerOrderCard.defaultProps = {
  compact: false,
};
